import { useState } from 'react'
import * as Sentry from '@sentry/react'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { auth } from '../lib/firebase'
import { sendPasswordResetEmail } from 'firebase/auth'
import { ChefHat, Loader, LogIn, UserPlus } from 'lucide-react'

export default function Login() {
  const { login, register } = useAuth()
  const { addToast } = useToast()
  const [mode, setMode] = useState('login') // 'login' | 'register' | 'forgot'
  const [submitting, setSubmitting] = useState(false)
  const [registeredMessage, setRegisteredMessage] = useState('')

  // Form fields
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [displayName, setDisplayName] = useState('')

  const resetForm = () => {
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setDisplayName('')
    setRegisteredMessage('')
  }

  const switchMode = (newMode) => {
    resetForm()
    setMode(newMode)
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    if (!email || !password) {
      addToast('Please fill in all fields', 'warning')
      return
    }
    setSubmitting(true)
    try {
      const profile = await login(email, password)
      if (profile && !profile.isApproved) {
        addToast('Your account is pending approval. Contact an admin.', 'warning')
      } else {
        addToast('Logged in successfully', 'success')
      }
    } catch (err) {
      const msg = err.code === 'auth/invalid-credential'
        ? 'Invalid email or password'
        : err.code === 'auth/user-not-found'
        ? 'No account found with this email'
        : err.code === 'auth/wrong-password'
        ? 'Incorrect password'
        : err.code === 'auth/too-many-requests'
        ? 'Too many attempts. Try again later.'
        : err.message || 'Login failed'
      addToast(msg, 'error')
      // Only capture non-user-error cases — bad password is expected
      // and would flood Sentry. Capture auth/too-many-requests, network
      // errors, and anything without a known auth/* code.
      if (!/^auth\/(invalid-credential|user-not-found|wrong-password)$/.test(err.code || '')) {
        Sentry.captureException(err, { extra: { fn: 'handleLogin', emailDomain: String(email).split('@')[1] || '' } })
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleRegister = async (e) => {
    e.preventDefault()
    if (!displayName || !email || !password || !confirmPassword) {
      addToast('Please fill in all fields', 'warning')
      return
    }
    if (password !== confirmPassword) {
      addToast('Passwords do not match', 'error')
      return
    }
    if (password.length < 6) {
      addToast('Password must be at least 6 characters', 'warning')
      return
    }
    setSubmitting(true)
    try {
      const result = await register(email, password, displayName)
      if (result.isFirstUser) {
        addToast('Account created! You are the first user and have been granted admin access.', 'success')
      } else {
        setRegisteredMessage('Registration successful. Please wait for admin approval.')
        addToast('Registration successful', 'success')
      }
    } catch (err) {
      const msg = err.code === 'auth/email-already-in-use'
        ? 'An account with this email already exists'
        : err.code === 'auth/weak-password'
        ? 'Password is too weak. Use at least 6 characters.'
        : err.message || 'Registration failed'
      addToast(msg, 'error')
      if (!/^auth\/(email-already-in-use|weak-password|invalid-email)$/.test(err.code || '')) {
        Sentry.captureException(err, { extra: { fn: 'handleRegister', emailDomain: String(email).split('@')[1] || '' } })
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Branding */}
        <div className="text-center mb-8">
          <img src="/logo.jpg" alt="Desres Kitchen" className="inline-block w-16 h-16 object-contain mb-2" />
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
            DESRES KITCHEN AND CONFECTIONERY
          </h1>
          <div className="w-48 h-px bg-gray-700 mx-auto" />
        </div>

        {/* Card */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          {/* Tabs */}
          <div className="flex mb-6">
            <button
              onClick={() => switchMode('login')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                mode === 'login'
                  ? 'bg-brand-700 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              <LogIn size={14} />
              Login
            </button>
            <button
              onClick={() => switchMode('register')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-lg ml-2 transition-colors ${
                mode === 'register'
                  ? 'bg-brand-700 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              <UserPlus size={14} />
              Register
            </button>
          </div>

          {/* Registration success message */}
          {registeredMessage && (
            <div className="bg-green-900/50 border border-green-700 rounded-lg px-4 py-3 mb-4 text-sm text-green-300">
              {registeredMessage}
            </div>
          )}

          {/* Login Form */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-500"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-500"
                  autoComplete="current-password"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                {submitting ? <Loader size={16} className="animate-spin" /> : <LogIn size={16} />}
                {submitting ? 'Signing in...' : 'Sign In'}
              </button>
              <button
                type="button"
                onClick={() => setMode('forgot')}
                className="w-full text-xs text-gray-500 hover:text-gray-300 mt-2"
              >
                Forgot password?
              </button>
            </form>
          )}

          {/* Forgot Password Form */}
          {mode === 'forgot' && (
            <form onSubmit={async (e) => {
              e.preventDefault()
              if (!email) { addToast('Enter your email address', 'warning'); return }
              setSubmitting(true)
              try {
                await sendPasswordResetEmail(auth, email)
                addToast('Password reset email sent. Check your inbox.', 'success')
                setMode('login')
              } catch (err) {
                addToast(err.code === 'auth/user-not-found' ? 'No account found with that email' : 'Failed to send reset email', 'error')
                if (err.code !== 'auth/user-not-found') {
                  Sentry.captureException(err, { extra: { fn: 'passwordReset' } })
                }
              } finally {
                setSubmitting(false)
              }
            }} className="space-y-4">
              <p className="text-xs text-gray-400 mb-2">Enter your email to receive a password reset link.</p>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white" placeholder="you@example.com" />
              </div>
              <button type="submit" disabled={submitting}
                className="w-full bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2">
                {submitting ? <Loader size={16} className="animate-spin" /> : null}
                {submitting ? 'Sending...' : 'Send Reset Email'}
              </button>
              <button type="button" onClick={() => setMode('login')} className="w-full text-xs text-gray-500 hover:text-gray-300">
                Back to login
              </button>
            </form>
          )}

          {/* Register Form */}
          {mode === 'register' && (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Display Name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-500"
                  autoComplete="name"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-500"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-500"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your password"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-500"
                  autoComplete="new-password"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                {submitting ? <Loader size={16} className="animate-spin" /> : <UserPlus size={16} />}
                {submitting ? 'Creating account...' : 'Create Account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
